import { useCallback } from "react";
import { useRouter } from "next/router";
import { toast } from "react-toastify";
import { Stripe } from "@stripe/stripe-js";
import {
  CheckoutCompleteData,
  useRecaptcha,
} from "@ecologi/react-components/src";
import {
  User,
  CAPTCHA_ACTIONS,
  ImpactShopBasketProduct,
  ImpactShopCheckoutProduct,
  Currency,
  Country,
} from "@ecologi/shared";
import {
  checkoutWithRegistration,
  clearAttributionCookie,
  formatAPIErrorForView,
  pushImpactShopSuccess,
} from "../../../lib";

interface BasketProductsResult {
  products: ImpactShopCheckoutProduct[];
  currency: Currency;
}

interface UseCheckoutWithRegistrationParams {
  stripe: Stripe | null;
  recaptcha: ReturnType<typeof useRecaptcha>;
  location: { countryCode: Country } | null;
  idempotencyKey: string;
  basket: ImpactShopBasketProduct[];
  basketProductsWithCurrency: () => BasketProductsResult;
  totalPriceOfBasket: () => number;
  sendAnalyticsEvent: (event: string, data?: Record<string, unknown>) => void;
  setUser: (user: User) => void;
  setUtmParams: (params: Record<string, string>) => void;
  resetIdempotencyKey: () => void;
  utmParams: Record<string, string | undefined>;
}

export function useCheckoutWithRegistration({
  stripe,
  recaptcha,
  location,
  idempotencyKey,
  basket,
  basketProductsWithCurrency,
  totalPriceOfBasket,
  sendAnalyticsEvent,
  setUser,
  setUtmParams,
  resetIdempotencyKey,
  utmParams,
}: UseCheckoutWithRegistrationParams) {
  const router = useRouter();

  const handleCheckoutWithRegistrationComplete = useCallback(
    async (data: CheckoutCompleteData) => {
      if (!stripe || !data.paymentMethodId) {
        toast.error("Payment could not be processed. Please try again.");
        return;
      }

      try {
        const captchaToken = await recaptcha.execute(
          CAPTCHA_ACTIONS.IMPACT_SHOP
        );

        const { products, currency: basketCurrency } =
          basketProductsWithCurrency();

        const response = await checkoutWithRegistration({
          customer: {
            emailAddress: data.formData.email,
            firstName: data.formData.firstName,
            lastName: data.formData.lastName,
            location: location?.countryCode || "GB",
          },
          billingAddress: {
            companyName: data.formData.billingAddress.companyName || undefined,
            address1: data.formData.billingAddress.address1,
            address2: data.formData.billingAddress.address2 || undefined,
            city: data.formData.billingAddress.city,
            postcode: data.formData.billingAddress.postcode,
            country: data.formData.billingAddress.country,
          },
          basket: {
            products: products.map((p) => ({
              productId: p.productId,
              quantity: p.quantity,
              fundedBy: p.fundedBy || undefined,
              recipientEmail: p.recipientEmail || undefined,
              groupId: p.groupId || undefined,
            })),
            currency: basketCurrency,
            idempotencyKey,
            utmParams:
              Object.keys(utmParams).length > 0 ? utmParams : undefined,
          },
          paymentMethodId: data.paymentMethodId,
          captchaToken,
          accountType: data.formData.accountType || undefined,
        });

        if (response.payment.status === "requires_action") {
          toast.error(
            "Additional authentication is required. Please try again or use a different card."
          );
          resetIdempotencyKey();
          return;
        }

        const analyticItems = basket.map((product) => ({
          productId: product.id,
          productName: product.display.title,
          quantity: product.quantity,
        }));

        const totalPriceForReporting = totalPriceOfBasket() / 100;

        pushImpactShopSuccess({
          revenue: totalPriceForReporting,
          currency: basketCurrency,
          id: idempotencyKey,
          items: analyticItems,
        });

        sendAnalyticsEvent("impactShopPurchase", {
          revenue: totalPriceForReporting,
          currency: basketCurrency,
        });

        setUser(new User(response.user));

        clearAttributionCookie();
        setUtmParams({});

        await router.push(`/impact-shop/checkout-success?type=card`);
      } catch (error) {
        resetIdempotencyKey();
        toast.error(formatAPIErrorForView(error));
      }
    },
    [
      stripe,
      recaptcha,
      location,
      idempotencyKey,
      basket,
      basketProductsWithCurrency,
      totalPriceOfBasket,
      sendAnalyticsEvent,
      setUser,
      setUtmParams,
      router,
      resetIdempotencyKey,
      utmParams,
    ]
  );

  return { handleCheckoutWithRegistrationComplete };
}
